import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
    useLocation,
    useParams,
    Route,
    Switch,
    useHistory,
} from 'react-router-dom';
import { getDataPromise } from 'brock-fetch-utils';
import Content from './BagInformation/Content';
import getUri from '../../utils/getUri';
import { fakedata } from './constants/fakeData';
import BagSearch from './Search/BagSearch';
import { orderBy } from 'natural-orderby';
import SkeletonContextProvider from './skeletonComponents/SkeletonContext';

function GetLatestBagId(data) {
    const orderedData = orderBy(data, (x) => x.bagSelectorResult.createdOn, [
        'desc',
    ]);

    return orderedData[0].bagSelectorResult.id;
}

export default function App() {
    const { t } = useTranslation('bagSummary');
    const { bagTag, id } = useParams();
    const history = useHistory();
    const location = useLocation();

    const [searchInput, setSearchInput] = useState(bagTag);

    const queryParams = new URLSearchParams(location.search);
    const timestamp = queryParams.get('timestamp');

    const {
        isFetching: isBagSummaryLoading,
        isError,
        data,
        error,
        refetch,
    } = useQuery(
        ['bagNodes', bagTag],
        () => {
            const uri = timestamp
                ? `BagSummary/GetBagSummaryWithTimestamp?bagTag=${bagTag}&timestamp=${timestamp}`
                : `BagSummary/GetBagSummary?bagTag=${bagTag}`;

            return getDataPromise(getUri(uri));
        },
        {
            enabled: !!bagTag,
            onSuccess: (data) => {
                if (data?.length) {
                    let path = `/BagSummary/${bagTag}/`;

                    if (id) {
                        path += `${id}`;
                    } else {
                        const latestBagId = GetLatestBagId(data);
                        path += `${latestBagId}`;
                    }

                    if (timestamp) {
                        path += `?timestamp=${timestamp}`;
                    }

                    history.push(path);
                }
            },
        }
    );

    const {
        isLoading: isLoadingUrl,
        isError: isUrlError,
        data: urldata,
        error: urlerror,
    } = useQuery([], () =>
        getDataPromise(getUri(`ExternalUri/GetExternalBaseUris`))
    );

    useEffect(() => {
        document.title = t('title');
    }, [t]);

    function onSearch() {
        const parsedSearchInput = searchInput.replace(/\s/g, '');
        if (bagTag === parsedSearchInput) {
            refetch();
        }
        else {
            history.push(`/BagSummary/${parsedSearchInput}`);
        }
    }

    function onBagSelect(bagId) {
        const parsedSearchInput = searchInput.replace(/\s/g, '');
        let path = `/BagSummary/${parsedSearchInput}/${bagId}`;

        if (timestamp) {
            path += `?timestamp=${timestamp}`;
        }

        history.push(path);
    }

    if (isError || isUrlError) {
        return error.message || urlerror.message;
    }

    return (
        <SkeletonContextProvider
            isLoading={isBagSummaryLoading || isLoadingUrl}
        >
            <div className="main">
                <BagSearch
                    data={data}
                    onBagSelect={onBagSelect}
                    onSearch={onSearch}
                    setSearchInput={setSearchInput}
                    searchInput={searchInput}
                ></BagSearch>
                <Switch>
                    <Route path={'/BagSummary/:bagTag/:id?'}>
                        <Content bags={data} urlData={urldata} />
                    </Route>
                </Switch>
            </div>
        </SkeletonContextProvider>
    );
}
